import { jsPDF } from 'jspdf';
import { drawYourTourHeader, drawYourTourFooter } from './yourTourPdfBranding';
import { drawMstHeader, drawMstFooter } from './mstPdfBranding';
import { computeDepositSchedule } from './depositSchedule';

const BLACK = [17, 24, 39];
const GRAY  = [107, 114, 128];

function fmt(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function setColor(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc, rgb)  { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

// Customer-facing items on the saved quote — internal-only lines (referral
// fee, staff costs) are excluded, same as generateQuoteLetterPdf.
function getIncludedItems(breakdown) {
  return (breakdown || []).filter(
    (r) => !['referral_fee', 'mst_staff_cost', 'davids_cost'].includes(r.pricingType)
  );
}

// MST's "What will your child enjoy?" list — MST tours are bespoke, so it's
// built from the tour's own saved quote rather than a fixed package. The
// confirmation letter passes matchTicketBullet: false since its "Added Costs"
// section already explains the match ticket in full.
export function buildMstIncludedBullets(request, { matchTicketBullet = true } = {}) {
  const quoteDetails = request.quoteDetails || {};
  const bullets = getIncludedItems(quoteDetails.breakdown).map((item) => {
    const annotation = request.addonAnnotations?.[item.categoryName];
    return annotation ? `${item.categoryName} — ${annotation}` : item.categoryName;
  });
  const leads = Number(quoteDetails.tourLeadCount) || 0;
  if (leads > 0) {
    bullets.push(`${leads} designated bilingual Tour Leader${leads !== 1 ? 's' : ''} for the duration of the itinerary`);
  }
  if (matchTicketBullet && (request.selectedAddons || []).some((a) => a.categoryName === 'Match Tickets')) {
    const comp = request.market === 'spain_to_uk' ? 'Premier League' : 'La Liga';
    bullets.push(`${comp} match ticket — price TBC, invoiced separately once the fixture is confirmed`);
  }
  return bullets;
}

/**
 * Generates the "1st Letter to Parents" quote letter as a PDF, for Your Tour
 * (UK → Spain / Costa Blanca package) and MST (UK → Spain, bespoke — branded
 * MST, with the included list built from the tour's quote). Replaces the
 * cost-breakdown-style quote letter for these brand/market combinations — see
 * usesParentLetters() in letterAssets.js, and generateQuoteLetterPdf.js for
 * the generic version this sits alongside. Returns a base64-encoded PDF string.
 */
export function generateYourTourQuoteLetterPdf(request, crestDataUrl = null, logoDataUrl = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const pageH = 297;
  const marginL = 18;
  const marginR = 18;
  const contentW = pageW - marginL - marginR;

  const sym = CURRENCY_SYMBOLS[request.currency] || '£';
  const quoteDetails = request.quoteDetails || {};
  const nights = quoteDetails.nights || 0;
  const costPerPerson = quoteDetails.costPerPersonToCustomer || 0;
  const payingPlayers = quoteDetails.payingPlayers ?? (Number(request.participants) || 0);
  const totalCost = quoteDetails.totalCostToCustomer ?? 0;
  const groupName = request.groupName || 'Your Club';
  const isMst = request.brand !== 'your_tour';
  const company = isMst ? 'MST Sports Tours' : 'Your Tour';
  const sport = request.sport || 'Football';
  const destination = request.destination || 'Spain';

  const schedule = computeDepositSchedule(payingPlayers, totalCost, request.departureDate);

  let y = 0;

  // ── Header ────────────────────────────────────────────────────────────────
  y = isMst
    ? drawMstHeader(doc, logoDataUrl, crestDataUrl, groupName)
    : drawYourTourHeader(doc, logoDataUrl, crestDataUrl, groupName);

  // ── Title ─────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bolditalic');
  doc.setFontSize(20);
  setColor(doc, BLACK);
  const title = isMst ? `${destination} ${sport.toLowerCase()} experience` : 'Spanish football experience';
  doc.text(title, pageW / 2, y, { align: 'center' });
  const tw = doc.getTextWidth(title);
  setDraw(doc, BLACK);
  doc.setLineWidth(0.5);
  doc.line((pageW - tw) / 2, y + 1, (pageW + tw) / 2, y + 1);
  y += 10;

  // ── Salutation ────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  setColor(doc, BLACK);
  doc.text('Dear Parent / Carer,', marginL, y);
  y += 8;

  // ── Paragraph 1 ──────────────────────────────────────────────────────────
  const p1 = isMst
    ? `We are delighted to inform you that ${groupName} have secured an MST Sports Tours ${destination} ${sport.toLowerCase()} experience and your child is now invited to register their interest for one of the limited spaces available.`
    : `We are delighted to inform you that ${groupName} have secured a Your Tour Spain, Costa Blanca football experience and your child is now invited to register their interest for one of the limited spaces available.`;
  doc.setFontSize(10);
  setColor(doc, BLACK);
  const p1lines = doc.splitTextToSize(p1, contentW);
  doc.text(p1lines, marginL, y);
  y += p1lines.length * 5.2 + 5;

  // ── Paragraph 2 ──────────────────────────────────────────────────────────
  const p2 = `Following conversations with club representatives, ${company} has designed a bespoke itinerary which we believe will provide your child and their teammates with a truly unforgettable experience.`;
  const p2lines = doc.splitTextToSize(p2, contentW);
  doc.text(p2lines, marginL, y);
  y += p2lines.length * 5.2 + 6;

  // ── What will your child enjoy? ───────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('What will your child enjoy?', marginL, y);
  y += 6;

  const mstBullets = isMst ? buildMstIncludedBullets(request) : null;
  const bullets = isMst ? (mstBullets.length ? mstBullets : ['Full itinerary to be confirmed']) : [
    `${nights || 'X'} night${nights !== 1 ? 's' : ''} accommodation at Your Tour Spain Accommodation`,
    '2 coaching sessions led by Spanish coaches - 1 with Coaches from Elche FC & 1 with Spanish RFEF Licensed Coaches',
    '2 friendly fixtures against local Spanish opposition or 1 fixture & 1 Inter School Mini tournament',
    '1 Stadium Tour of Valencia, Levante or Elche Stadium or',
    '1 Fun based team building activity - Paintballing/ Escape Room',
    '2 specialist football related activity such as yoga or strength training',
    '1 Alicante City Day - Alicante City Selfie Hunt & Santa Barbara',
    '1 Valencia City Day - Science Park and Old Town',
    '1 Beach yoga, S&C and Volleyball',
    '1 executive transport with a driver for the duration of your itinerary',
    'All meals (x3 breakfasts, x3 lunches, x3 evening meals and healthy snacks).',
    'Return flights - 10kg hand luggage and small bag',
  ];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setColor(doc, BLACK);
  bullets.forEach((bullet) => {
    const lines = doc.splitTextToSize(bullet, contentW - 6);
    if (y + lines.length * 5 > pageH - 15) { doc.addPage(); y = 20; }
    doc.text('•', marginL + 1, y);
    doc.text(lines, marginL + 5, y);
    y += lines.length * 5 + 1.5;
  });
  y += 3;

  // ~95mm needed from here through "Secure Your Tour today" and the footer —
  // nothing below auto-paginates otherwise, so a long bullet list (Your Tour
  // always has 12; MST's varies with the quote) can easily push the rest off
  // the bottom of the page.
  if (y > pageH - 95) { doc.addPage(); y = 20; }

  // ── Additional costs line ─────────────────────────────────────────────────
  const numPlayers = request.participants != null ? request.participants : 'X';
  const numStaff = request.customerStaff != null ? request.customerStaff : 'X';
  const addCostsText = `The only additional costs are for travel insurance, and spending money which are to be organised by your club and parents / carers. Prices are based on ${numPlayers} students and ${numStaff} staff.`;
  const addCostsLines = doc.splitTextToSize(addCostsText, contentW);
  doc.text(addCostsLines, marginL, y);
  y += addCostsLines.length * 5.2 + 6;

  // ── What is the cost and when are the dates? ─────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('What is the cost and when are the dates?', marginL, y);
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const datesLine = `Dates: ${formatDate(request.departureDate)} - ${formatDate(request.returnDate)}`;
  doc.text(datesLine, marginL, y);
  y += 6;

  // MST quotes don't always include flights — when they do, Flights is in
  // the included list above.
  const withFlightsLabel = isMst ? 'Price: ' : 'With Flights: ';
  const withFlightsValue = `${fmt(costPerPerson, sym)} per person`;
  doc.setFont('helvetica', 'bold');
  doc.text(withFlightsLabel, marginL, y);
  const withFlightsLabelW = doc.getTextWidth(withFlightsLabel);
  doc.setFont('helvetica', 'normal');
  doc.text(withFlightsValue, marginL + withFlightsLabelW, y);
  y += 9;

  // ── Secure Your Tour today! ───────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text(isMst ? 'Secure your place today!' : 'Secure Your Tour today!', marginL, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const securePre = 'Places are strictly limited and offered on a first come first served basis. To secure a place for your child and you (or a family member), simply return a ';
  const securePreLines = doc.splitTextToSize(securePre, contentW);
  doc.text(securePreLines, marginL, y);
  y += securePreLines.length * 5.2;

  doc.setFont('helvetica', 'bold');
  const amtText = `${sym}100.00`;
  doc.text(amtText, marginL, y);
  doc.setFont('helvetica', 'normal');
  const afterAmt = ' per person to your designated club contact before ';
  doc.text(afterAmt, marginL + doc.getTextWidth(amtText), y);
  doc.setFont('helvetica', 'bold');
  doc.text(formatDate(schedule.depositDueDate), marginL + doc.getTextWidth(amtText) + doc.getTextWidth(afterAmt), y);
  y += 7;

  const balanceText = `Your remaining balance will be due in two parts of ${fmt(schedule.instalment2Amt, sym)} on ${formatDate(schedule.instalment2DueDate)} and ${fmt(schedule.instalment3Amt, sym)} on ${formatDate(schedule.finalDueDate)} respectively.`;
  const balanceLines = doc.splitTextToSize(balanceText, contentW);
  doc.text(balanceLines, marginL, y);
  y += balanceLines.length * 5.2 + 6;

  // ── Closing line ──────────────────────────────────────────────────────────
  doc.setFontSize(9.5);
  setColor(doc, GRAY);
  doc.text(`${company} welcomes your questions and contact details are at the head of this page.`, marginL, y);

  // ── Footer ────────────────────────────────────────────────────────────────
  if (isMst) drawMstFooter(doc, y, logoDataUrl);
  else drawYourTourFooter(doc, y, logoDataUrl);

  return doc.output('datauristring').split(',')[1];
}
